/**
 * CourseSelectorDrawer — picks the course (terrain) for a running prediction.
 *
 * Tabs:
 *   Races          — catalog races (with GPX)
 *   Upload GPX     — one-off GPX file upload
 *   My Running Files — user's own FIT files (used as course terrain)
 *   Club Files     — FIT files shared to the user's clubs
 *
 * onSelect(source) shape:
 *   { type: 'race',     raceId, record }
 *   { type: 'gpxUpload', file }
 *   { type: 'myfile',   fileId, record }
 *   { type: 'clubFile', clubId, clubFileId, filename, record }
 */

import { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import WorkoutPickerDrawer from './WorkoutPickerDrawer';
import { getMyClubFiles, downloadClubFile } from '../../api/clubApi';

const clubFilesColumns = [
  { field: 'filename',          headerName: 'File',      flex: 2, minWidth: 160 },
  { field: 'club_name',         headerName: 'Club',      flex: 1, minWidth: 100 },
  {
    field: 'workout_date', headerName: 'Date', width: 90,
    valueFormatter: (v) => v ? new Date(v).toLocaleDateString() : '—',
  },
  {
    field: 'total_distance', headerName: 'Dist', width: 80,
    valueFormatter: (v) => v != null ? `${(Number(v) / 1000).toFixed(1)} km` : '—',
  },
  {
    field: 'elevation_gain', headerName: '↑', width: 60,
    valueFormatter: (v) => v ? `+${Math.round(v)}m` : '—',
  },
  {
    // Club owners (admins) can download the original FIT; hidden for everyone else.
    field: 'download', headerName: '', width: 48, sortable: false, filterable: false,
    renderCell: (params) => params.row.can_download ? (
      <Tooltip title="Download FIT file">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            downloadClubFile(params.row.club_id, params.row.id, params.row.filename);
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null,
  },
];

export default function CourseSelectorDrawer({
  open,
  onClose,
  onSelect,
  withPredictions = false,
  predictionsFilter = null,
}) {
  const [clubFiles, setClubFiles]         = useState([]);
  const [clubFilesLoading, setLoading]    = useState(false);
  const [clubFilesLoaded, setLoaded]      = useState(false);

  const handleTabChange = (tabId) => {
    if (tabId === 'clubFiles' && !clubFilesLoaded && !clubFilesLoading) {
      setLoading(true);
      getMyClubFiles()
        .then((data) => {
          const rows = (Array.isArray(data) ? data : []).map((f) => ({
            ...f,
            id: f.id ?? f.club_file_id,
          }));
          setClubFiles(rows);
          setLoaded(true);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  };

  // Reset club-file cache when drawer closes so fresh data appears next open.
  const handleClose = () => {
    setLoaded(false);
    setClubFiles([]);
    onClose();
  };

  const extraTabs = [
    {
      id: 'clubFiles',
      label: 'Club Files',
      rows: clubFiles,
      columns: clubFilesColumns,
      loading: clubFilesLoading,
      getSource: (row) => ({
        type: 'clubFile',
        clubId: row.club_id,
        clubFileId: row.id,
        filename: row.filename,
        record: row,
      }),
    },
  ];

  const tabs = [
    'races',
    'gpxUpload',
    ...(withPredictions ? ['predictions'] : []),
    'myFiles',
  ];

  return (
    <WorkoutPickerDrawer
      open={open}
      onClose={handleClose}
      title="Select Course"
      tabs={tabs}
      extraTabs={extraTabs}
      defaultTab="races"
      actionLabel="Use as Course"
      onSelect={onSelect}
      onTabChange={handleTabChange}
      predictionsFilter={predictionsFilter}
    />
  );
}
